import { onCleanup, onMount, Show } from 'solid-js'
import { computeAcoustics } from './acoustics/compute'
import { measureIr } from './audio/measure'
import { renderClick, renderScene } from './audio/offline'
import { AppProvider, createApp } from './controller'
import { createPreset, makePrimitive, makeSource } from './model/presets'
import { Inspector } from './ui/inspector'
import { Dock, Outliner, Toasts, TopBar, ViewportPanel } from './ui/shell'

const isTyping = (t: EventTarget | null) => {
  const el = t as HTMLElement | null
  if (!el) return false
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable
}

export function App() {
  const app = createApp()
  const { store, ui, setUi } = app

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      // Sliders keep focus after a drag; letter shortcuts still work there, but Delete/Backspace must not remove the selection.
      const onRange = (e.target as HTMLElement | null)?.matches?.('input[type=range]') ?? false
      if (isTyping(e.target) && !onRange) return
      const mod = e.ctrlKey || e.metaKey
      if (ui.mode === 'explore') {
        if (e.key === 'Escape') app.getViewport()?.exitExplore()
        return
      }
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault()
        if (e.shiftKey) store.redo()
        else store.undo()
      } else if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault()
        store.redo()
      } else if (mod && e.key.toLowerCase() === 'd') {
        e.preventDefault()
        app.duplicateSelection()
      } else if (!mod && (e.key === 'Delete' || e.key === 'Backspace')) {
        if (onRange) return
        e.preventDefault()
        app.deleteSelection()
      } else if (!mod && e.key.toLowerCase() === 'w') setUi('transformMode', 'translate')
      else if (!mod && e.key.toLowerCase() === 'e') setUi('transformMode', 'rotate')
      else if (!mod && e.key.toLowerCase() === 'r') setUi('transformMode', 'scale')
      else if (!mod && e.key.toLowerCase() === 'c') app.clickTest()
      else if (!mod && e.key.toLowerCase() === 'f') app.getViewport()?.frameScene()
    }
    window.addEventListener('keydown', onKey)
    onCleanup(() => window.removeEventListener('keydown', onKey))

    // Test/debug hooks (used by the Playwright suite and for listening checks from the console).
    ;(window as unknown as Record<string, unknown>).__megaspace = {
      app,
      renderClick,
      renderScene,
      computeAcoustics,
      measureIr,
      createPreset,
      makePrimitive,
      makeSource,
    }
  })

  return (
    <AppProvider value={app}>
      <Show when={app.ready()} fallback={<div class="boot">Loading…</div>}>
        <div class="shell" data-mode={ui.mode} data-audio={ui.audioStatus}>
          <TopBar />
          <Outliner />
          <main class="center">
            <ViewportPanel />
            <Dock />
          </main>
          <Inspector />
          <Toasts />
        </div>
      </Show>
    </AppProvider>
  )
}
